import { useEstiloDeImpresion } from '../experiencia/estiloDeImpresion';
import { formatearCantidad } from '../experiencia/cantidades';

// El ticket que se lleva el cliente.
//
// QUE ES Y QUE NO ES. Esto es el papel del mostrador, no un comprobante
// fiscal: en México un ticket de venta no es un CFDI, y un papel que
// parece un recibo sin decir que no lo es manda al cliente a su
// contabilidad con algo que no le sirve. Lo dice al pie, con esas
// palabras.
//
// LO QUE NO LLEVA, y es la razón de que este componente exista aparte del
// detalle de Ventas: márgenes, costos y quién autorizó el descuento. Esa
// pantalla los enseña a propósito —es la vista de auditoría— y son
// exactamente los datos que no pueden salir impresos por el mostrador.
//
// ESTO NO ES ESC/POS. Va por el diálogo de impresión del navegador, que
// sirve para una impresora normal y para una térmica que tenga
// controlador del sistema. Mandar bytes a una térmica por USB o red es
// otro trabajo, y sigue anotado como tal.
const ANCHO_ROLLO = '72mm';   // 80 mm de papel menos los márgenes

const dinero = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
    .format(Number(n || 0));

// Una sola forma de escribir una cantidad en todo el sistema (T-17).
const cantidad = (n, unidad) => formatearCantidad(n, unidad);

// AGRUPA LAS LINEAS REPETIDAS. Escanear tres veces el mismo producto son
// tres renglones en la venta —y asi los guarda el kardex, que es lo
// correcto— pero en el papel del cliente tres lineas identicas se leen
// como un error de cobro. Se agrupa por SKU **y precio**: dos unidades
// del mismo producto a precios distintos son dos cosas distintas para
// quien mira el ticket, y juntarlas escondería justamente la diferencia
// que haría preguntar.
function agrupar(renglones) {
  const por = new Map();
  for (const r of renglones) {
    const clave = `${r.sku}|${r.precio_unitario}`;
    const ya = por.get(clave);
    if (ya) {
      ya.cantidad = Number(ya.cantidad) + Number(r.cantidad);
      ya.importe = Number(ya.importe) + Number(r.importe);
    } else {
      por.set(clave, { ...r, cantidad: Number(r.cantidad), importe: Number(r.importe) });
    }
  }
  return [...por.values()];
}

const METODOS = {
  EFECTIVO: 'Efectivo', TARJETA: 'Tarjeta', TRANSFERENCIA: 'Transferencia',
  MIXTO: 'Mixto',
};

/**
 * Instala el estilo de impresión mientras el ticket está montado.
 *
 * Se hace con un `<style>` inyectado y no con clases de Tailwind porque
 * `@page` no se puede expresar como utilidad: es el que decide que el
 * papel sea un rollo de 80 mm y no una hoja A4 con el ticket en una
 * esquina.
 */
function useEstiloImpresion() {
  // Hoja construida, no <style>: la CSP de producción tira el <style>
  // (ver experiencia/estiloDeImpresion.js, 19 sep 2026).
  useEstiloDeImpresion(`
      @media print {
        @page { size: ${ANCHO_ROLLO} auto; margin: 3mm; }
        body * { visibility: hidden; }
        #ticket-venta, #ticket-venta * { visibility: visible; }
        #ticket-venta {
          position: absolute; left: 0; top: 0;
          width: ${ANCHO_ROLLO}; margin: 0; padding: 0;
          font-family: ui-monospace, "Courier New", monospace;
          font-size: 11px; line-height: 1.35; color: #000;
        }
        .sin-imprimir { display: none !important; }
      }
    `);
}

// LA VENTA QUE TODAVÍA NO TIENE FOLIO · DIRECTRIZ-PWA F2 (11 oct 2026,
// DISENO-F2 §4.8; parche del integrador para experiencia). Cobrada sin
// señal, la venta vive en el teléfono hasta que se envía: el papel lo dice
// con esas palabras, imprime la hora del teléfono y anota cuándo se guardó.
// Mismo papel, mismos importes; sin `pendiente`, idéntico a siempre.
const hora = (d) => d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

export default function TicketVenta({ venta, copia = false }) {
  useEstiloImpresion();

  const fecha = venta.fecha ? new Date(venta.fecha) : null;
  const pendiente = venta.pendiente === true;
  const guardada = pendiente && venta.guardada_en ? new Date(venta.guardada_en) : null;

  return (
    <div id="ticket-venta" className="text-sm text-gray-900">
      {/* UNA REIMPRESIÓN SE DICE. Sin esto circulan dos papeles idénticos
          de la misma venta, y la misma devolución se puede reclamar dos
          veces con cara de primera vez. */}
      {copia && (
        <div className="text-center font-bold tracking-widest border-y border-black py-1 mb-1">
          REIMPRESIÓN
        </div>
      )}

      {/* Y una venta cancelada, más todavía: imprimirla como si valiera
          es entregar un comprobante de algo que no ocurrió. */}
      {venta.estado === 'CANCELADA' && (
        <div className="text-center font-bold tracking-widest border-y border-black py-1 mb-1">
          CANCELADA
        </div>
      )}

      <div className="text-center leading-tight mb-2">
        <div className="font-bold">{venta.sucursal || 'Mostrador'}</div>
        {pendiente ? (
          <div>
            <span className="font-bold">Pendiente de enviar</span> · el folio lo da TEGO al volver la señal
          </div>
        ) : (
          <div>Venta {venta.folio}</div>
        )}
        {fecha && (
          <div>
            {fecha.toLocaleDateString('es-MX')} {hora(fecha)}
            {pendiente && ' (hora del teléfono)'}
          </div>
        )}
        {venta.cajero && <div>Atendió: {venta.cajero}</div>}
        {venta.cliente_nombre && <div>Cliente: {venta.cliente_nombre}</div>}
      </div>

      <div className="border-t border-b border-gray-400 py-1">
        {agrupar(venta.renglones).map((r, i) => (
          <div key={i} className="flex justify-between gap-2 py-0.5">
            <span className="min-w-0">
              <span className="block">{r.producto || r.sku}</span>
              <span className="block text-xs">
                {cantidad(r.cantidad, r.unidad_medida)} × {dinero(r.precio_unitario)}
              </span>
            </span>
            <span className="tabular-nums whitespace-nowrap">{dinero(r.importe)}</span>
          </div>
        ))}
      </div>

      <div className="py-1 space-y-0.5">
        <Renglon etiqueta="Subtotal" valor={dinero(venta.subtotal)} />
        {Number(venta.descuento_total) > 0 && (
          <Renglon etiqueta="Descuentos" valor={`-${dinero(venta.descuento_total)}`} />
        )}
        <Renglon etiqueta="IVA" valor={dinero(venta.impuestos)} />
        <div className="flex justify-between border-t border-gray-400 pt-1 font-bold">
          <span>TOTAL</span>
          <span className="tabular-nums">{dinero(venta.total)}</span>
        </div>
      </div>

      <div className="py-1 space-y-0.5 border-t border-gray-400">
        <Renglon etiqueta="Forma de pago"
                 valor={METODOS[venta.metodo_pago] || venta.metodo_pago} />
        {venta.efectivo_recibido != null && (
          <Renglon etiqueta="Recibido" valor={dinero(venta.efectivo_recibido)} />
        )}
        {venta.cambio != null && (
          <Renglon etiqueta="Cambio" valor={dinero(venta.cambio)} />
        )}
      </div>

      {/* NO ES UN CFDI, y se dice. Un papel que parece un recibo sin
          aclararlo manda al cliente a su contabilidad con algo que no le
          sirve, y eso se descubre en el peor momento del año. */}
      <div className="text-center text-xs mt-2 pt-1 border-t border-gray-400 leading-tight">
        {guardada && <div>Guardado en este teléfono a las {hora(guardada)}</div>}
        <div>Comprobante de venta — no es un comprobante fiscal (CFDI).</div>
        <div>Conserva este ticket para cualquier aclaración.</div>
      </div>
    </div>
  );
}

function Renglon({ etiqueta, valor }) {
  return (
    <div className="flex justify-between gap-2">
      <span>{etiqueta}</span>
      <span className="tabular-nums">{valor}</span>
    </div>
  );
}
